import { z } from 'zod';
import { sendJson, setAuthorizationProvider } from '@/lib/api';

/**
 * The embedded proposal editor's session: the scoped Editor token Salesforce
 * minted (`POST /api/v3/salesforce/proposals/:id/editor-session`), carried in
 * the iframe URL's fragment as `#editorToken=…`.
 *
 * The token lives in memory only — never localStorage or a cookie (a cookie
 * would be third-party in the Salesforce frame anyway, which is why this
 * exists). Reloading the frame therefore ends the session; Salesforce opens a
 * new one on the next Edit.
 */

/** The fragment key the API's mint URL carries the token in. */
export const EDITOR_TOKEN_FRAGMENT = 'editorToken';

/** The embedded editor's route prefix in the staff app. */
export const EMBEDDED_EDITOR_PREFIX = '/embed/proposals/';

/** The Authorization scheme the API's editor gate reads. */
export const EDITOR_AUTH_SCHEME = 'Editor';

/** Refresh this long before the token lapses (tokens last 30 minutes, so at ~25). */
export const REFRESH_LEAD_MS = 5 * 60_000;

/** After a refresh that failed for a reason other than "session ended", try again this soon. */
export const REFRESH_RETRY_MS = 30_000;

/** What the editor tells the Salesforce window. */
export const EDITOR_MESSAGE = {
  done: 'bce-proposal-editor-done',
  saved: 'bce-proposal-editor-saved',
} as const;
export type EditorMessageType = (typeof EDITOR_MESSAGE)[keyof typeof EDITOR_MESSAGE];

/** The proposal id from `/embed/proposals/<id>`, or null for any other path. */
export function proposalIdFromPath(pathname: string): string | null {
  if (!pathname.startsWith(EMBEDDED_EDITOR_PREFIX)) return null;
  const id = pathname.slice(EMBEDDED_EDITOR_PREFIX.length).replace(/\/+$/, '');
  return /^[a-fA-F0-9]{24}$/.test(id) ? id : null;
}

const tokenPayloadSchema = z.object({ exp: z.number() });

/**
 * When a token lapses, read from its payload. NOT a verification — the API
 * verifies every request; this only schedules the refresh.
 *
 * @returns The expiry, or null for something that is not a readable token.
 */
export function tokenExpiry(token: string): Date | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json: unknown = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    const parsed = tokenPayloadSchema.safeParse(json);
    return parsed.success ? new Date(parsed.data.exp * 1000) : null;
  } catch {
    return null;
  }
}

/** How long to wait before refreshing a token that lapses at `expiresAt`. */
export function refreshDelayMs(expiresAt: Date, now: Date): number {
  return Math.max(0, expiresAt.getTime() - now.getTime() - REFRESH_LEAD_MS);
}

/** Minimal views of the browser objects the session touches, so tests pass their own. */
export interface SessionLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}
export interface SessionHistory {
  replaceState(data: unknown, unused: string, url?: string): void;
}

/**
 * Takes the token out of the URL fragment and removes it from the address
 * bar at once, so it never sits in the frame's history.
 *
 * @returns The token, or null when the fragment has none.
 */
export function takeTokenFromUrl(location: SessionLocation, history: SessionHistory): string | null {
  const raw = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const token = params.get(EDITOR_TOKEN_FRAGMENT);
  if (token === null) return null;
  params.delete(EDITOR_TOKEN_FRAGMENT);
  const rest = params.toString();
  try {
    history.replaceState(null, '', `${location.pathname}${location.search}${rest ? `#${rest}` : ''}`);
  } catch {
    // A blocked history API leaves the fragment visible; the token still works.
  }
  return token === '' ? null : token;
}

/** One CSP host-source (`https://host`, `https://*.host`, optional port) as parts. */
interface AncestorSource {
  readonly scheme: string;
  readonly host: string;
  readonly wildcard: boolean;
  readonly port: string;
}

function parseSource(source: string): AncestorSource | null {
  const match = /^(https?):\/\/(\*\.)?([a-z0-9.-]+)(?::(\d+))?\/?$/i.exec(source.trim());
  if (!match) return null;
  const [, scheme = '', star, host = '', port = ''] = match;
  return { scheme: scheme.toLowerCase(), host: host.toLowerCase(), wildcard: star !== undefined, port };
}

/** The app server's frame-ancestors list (space- or comma-separated) as host-sources. */
export function parseFrameAncestors(raw: string | undefined): readonly string[] {
  return (raw ?? '').split(/[\s,]+/).filter((source) => parseSource(source) !== null);
}

/** Whether an origin is one the frame-ancestors list allows (`*.` covers subdomains, as in CSP). */
export function originAllowed(origin: string, ancestors: readonly string[]): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  const scheme = url.protocol.replace(/:$/, '');
  const host = url.hostname.toLowerCase();
  return ancestors.some((raw) => {
    const source = parseSource(raw);
    if (!source || source.scheme !== scheme || source.port !== url.port) return false;
    return source.wildcard ? host.endsWith(`.${source.host}`) : host === source.host;
  });
}

/**
 * The Salesforce window to post to: the referrer's origin (the page that set
 * the iframe's src), else the browser's view of the parent where it offers
 * one — and only when the frame-ancestors list allows it. Null means post
 * nothing: a message never goes to a window we cannot name.
 */
export function parentOrigin(
  referrer: string,
  ancestors: readonly string[],
  ancestorOrigins: readonly string[] = [],
): string | null {
  const candidates = [referrer, ancestorOrigins[0] ?? ''];
  for (const candidate of candidates) {
    if (!candidate) continue;
    let origin: string;
    try {
      origin = new URL(candidate).origin;
    } catch {
      continue;
    }
    if (originAllowed(origin, ancestors)) return origin;
  }
  return null;
}

/** A token and when it lapses. */
export interface EditorToken {
  readonly token: string;
  readonly expiresAt: Date;
}

const refreshResponseSchema = z.object({ token: z.string().min(1), expiresAt: z.iso.datetime({ offset: true }) });

/** The next token in this session, from the API (with the current token). */
export async function refreshEditorToken(proposalId: string): Promise<EditorToken> {
  const data = refreshResponseSchema.parse(await sendJson<unknown>('post', `/proposals/${proposalId}/editor-token/refresh`));
  return { token: data.token, expiresAt: new Date(data.expiresAt) };
}

/** The live session the embedded editor reads and the refresh loop updates. */
export interface EditorSession {
  readonly proposalId: string;
  current(): EditorToken;
  replace(next: EditorToken): void;
}

/**
 * Reads the token from the URL, strips it, and makes the shared API client
 * send it as `Authorization: Editor <token>` (through the client's own
 * provider seam). Must run before the first API call.
 *
 * @returns The session, or null when there is no usable token (the editor then shows "session ended").
 */
export function installEditorSession(
  location: SessionLocation = window.location,
  history: SessionHistory = window.history,
): EditorSession | null {
  const token = takeTokenFromUrl(location, history);
  const proposalId = proposalIdFromPath(location.pathname);
  // Whatever happens next, no staff credential is ever sent from this page.
  setAuthorizationProvider(() => Promise.resolve(null));
  const expiresAt = token ? tokenExpiry(token) : null;
  if (!token || !proposalId || !expiresAt) return null;
  let live: EditorToken = { token, expiresAt };
  setAuthorizationProvider(() => Promise.resolve(`${EDITOR_AUTH_SCHEME} ${live.token}`));
  return {
    proposalId,
    current: () => live,
    replace: (next) => {
      live = next;
    },
  };
}
